import { Navigate, useParams } from "react-router-dom";
import { Seo } from "@/components/Seo";
import { JsonLd } from "@/components/JsonLd";
import { t as getFixedT } from "@/i18n";
import {
  isSupportedLanguage,
  localeUrl,
  type SupportedLanguage,
} from "@/lib/site";
import { buildFaqJsonLd, buildOrganizationJsonLd } from "@/lib/structuredData";
import { Header } from "@/sections/Header";
import { Hero } from "@/sections/Hero";
import { WhyUs } from "@/sections/WhyUs";
import { Programs } from "@/sections/Programs";
import { ParentsOpinions } from "@/sections/ParentsOpinions";
import { LeadFormSection } from "@/sections/LeadFormSection";
import { Faq, type FaqItem } from "@/sections/Faq";
import { Footer } from "@/sections/Footer";
import { StickyCta } from "@/sections/StickyCta";
import { BackToTop } from "@/sections/BackToTop";
import "./HomePage.css";

/**
 * The real public landing page (T35) — prerendered per locale at `/ar` and `/en`
 * (`src/app/routes.tsx` `getStaticPaths`). Single scrollable page composed of the sections in
 * `docs/design/09-landing-page.md` §1/§2; every piece of copy comes from `src/i18n/locales/*.json`
 * (no hardcoded strings), and the whole tree here renders the same on the server (prerender pass)
 * and the client (hydration) — only the grade/school lookups inside `LeadForm` are client-fetched.
 */
export function HomePage() {
  const { lang } = useParams<{ lang: string }>();

  if (!isSupportedLanguage(lang)) {
    return <Navigate to="/ar" replace />;
  }

  return <LocaleHome lang={lang} />;
}

function LocaleHome({ lang }: { lang: SupportedLanguage }) {
  const t = getFixedT(lang);
  const otherLang: SupportedLanguage = lang === "ar" ? "en" : "ar";
  const faqItems = t("faq.items", { returnObjects: true }) as FaqItem[];

  return (
    <>
      <Seo
        lang={lang}
        title={t("meta.title")}
        description={t("meta.description")}
        canonical={localeUrl(lang)}
      />
      <JsonLd data={buildOrganizationJsonLd(lang)} />
      <JsonLd data={buildFaqJsonLd(faqItems)} />

      <a href="#main-content" className="fa-skip-link">
        {t("a11y.skipLink")}
      </a>

      <Header lang={lang} otherLang={otherLang} />

      <main id="main-content">
        <Hero lang={lang} />
        <WhyUs lang={lang} />
        <Programs lang={lang} />
        <ParentsOpinions lang={lang} />
        <LeadFormSection lang={lang} />
        <Faq lang={lang} />
      </main>

      <Footer lang={lang} otherLang={otherLang} />
      <StickyCta lang={lang} />
      <BackToTop lang={lang} />
    </>
  );
}
